import type { AdminCrewReportImportSummaryProps } from "../../models/admin-crew-report.models";
import {
  ADMIN_CREW_REPORT_IMPORT_CONTINUATION_MESSAGE,
  ADMIN_CREW_REPORT_IMPORT_INTERRUPTED_MESSAGE,
} from "../../utils/admin-crew-report.constants";
import AdminCrewReportMasterFeedbackSummary from "./AdminCrewReportMasterFeedbackSummary";

const AdminCrewReportImportSummary = ({ result }: AdminCrewReportImportSummaryProps) => {
  const firstReport = result.reports[0];
  if (!firstReport) return null;
  const finalReport = result.reports[result.reports.length - 1]!;
  const displayedCompletionTimestamp = result.interruption ? finalReport.completedAt : result.completedAt;
  const isSingleBatch = result.batchCount === 1;
  const permanentFailures = result.reports.reduce(
    (count, report) => count + report.items.filter(({ outcome }) => outcome === "FAILED").length,
    0,
  );
  const retryableFailures = result.reports.reduce(
    (count, report) => count + report.items.filter(({ outcome }) => outcome === "RETRYABLE_FAILURE").length,
    0,
  );

  const counts = [
    ["Matching emails", result.counts.scannedMessages],
    ["Excel attachments", result.counts.candidateAttachments],
    ["Imported", result.counts.imported],
    ["Failed", result.counts.failed],
    ["Superseded", result.counts.superseded],
    ["Already processed", result.counts.alreadyProcessed],
  ] as const;

  return (
    <div className="admin-crew-report-import-print-summary grid gap-4">
      <dl className="admin-crew-report-import-print-kpis grid grid-cols-2 gap-px bg-[#D8E6E3] dark:bg-gray-700 sm:grid-cols-3 lg:grid-cols-6">
        {counts.map(([label, value]) => (
          <div key={label} className="bg-[#F8FBFA] px-3 py-3 dark:bg-gray-800">
            <dt className="text-[11px] font-semibold uppercase text-[#65706F] dark:text-gray-400">{label}</dt>
            <dd className="mt-1 text-2xl font-semibold">{value}</dd>
            {label === "Failed" && value > 0 && (
              <dd className="mt-1 text-[11px] text-[#65706F] dark:text-gray-400">
                {permanentFailures.toLocaleString()} permanent · {retryableFailures.toLocaleString()} retryable
              </dd>
            )}
          </div>
        ))}
      </dl>

      <dl className="admin-crew-report-import-print-run-details grid gap-3 text-xs text-[#596563] dark:text-gray-300 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <dt className="font-semibold uppercase">Environment</dt>
          <dd>{firstReport.environment}</dd>
        </div>
        <div>
          <dt className="font-semibold uppercase">Mailbox</dt>
          <dd className="break-all">{firstReport.mailbox}</dd>
        </div>
        <div>
          <dt className="font-semibold uppercase">Triggered by</dt>
          <dd className="break-all">{firstReport.triggeredBy}</dd>
        </div>
        <div>
          <dt className="font-semibold uppercase">Run started</dt>
          <dd className="break-all">
            <time dateTime={result.startedAt}>{result.startedAt}</time>
          </dd>
        </div>
        <div>
          <dt className="font-semibold uppercase">{result.interruption ? "Last completed batch" : "Run completed"}</dt>
          <dd className="break-all">
            <time dateTime={displayedCompletionTimestamp}>{displayedCompletionTimestamp}</time>
          </dd>
        </div>
        {(!isSingleBatch || result.interruption) && (
          <div>
            <dt className="font-semibold uppercase">{result.interruption ? "Completed batches" : "Batches"}</dt>
            <dd>{result.batchCount.toLocaleString()}</dd>
          </div>
        )}
        <div>
          <dt className="font-semibold uppercase">Run cutoff</dt>
          <dd className="break-all">
            <time dateTime={result.runCutoffAt}>{result.runCutoffAt}</time>
          </dd>
        </div>
        <div>
          <dt className="font-semibold uppercase">Broader authenticated mailbox import</dt>
          <dd>{firstReport.allowTokenlessInitialImports ? "Enabled for this run" : "Disabled"}</dd>
        </div>
        <div>
          <dt className="font-semibold uppercase">Candidate limit</dt>
          <dd>{firstReport.candidateLimit.toLocaleString()} Excel attachments per batch</dd>
        </div>
        <div>
          <dt className="font-semibold uppercase">Continuation pending</dt>
          <dd>{result.interruption ? "Interrupted; rerun required" : result.continuationPending ? "Yes" : "No"}</dd>
        </div>
        <div>
          <dt className="font-semibold uppercase">Retryable items pending</dt>
          <dd>{result.retryableItemsPending ? "Yes" : "No"}</dd>
        </div>
        {isSingleBatch && (
          <>
            <div>
              <dt className="font-semibold uppercase">Cycle ID</dt>
              <dd className="break-all">{firstReport.cycleId}</dd>
            </div>
            {firstReport.resumedContinuation && (
              <div>
                <dt className="font-semibold uppercase">Resumed continuation</dt>
                <dd>Yes</dd>
              </div>
            )}
            <div className="sm:col-span-2">
              <dt className="font-semibold uppercase">Mailbox window</dt>
              <dd className="break-all">
                <time dateTime={firstReport.windowStartedAt}>{firstReport.windowStartedAt}</time> –{" "}
                <time dateTime={firstReport.windowEndedAt}>{firstReport.windowEndedAt}</time>
              </dd>
            </div>
          </>
        )}
      </dl>

      <AdminCrewReportMasterFeedbackSummary summary={result.masterFeedbackNotifications} />

      {result.interruption && (
        <div
          role="alert"
          className="rounded border border-[#BF0002] bg-[#FDECEC] px-3 py-2 text-xs text-[#8F0002] dark:border-red-700 dark:bg-red-950/40 dark:text-red-100"
        >
          <p className="font-semibold">
            Batch {result.interruption.failedBatchNumber} could not be completed: {result.interruption.reason}
          </p>
          <p className="mt-1">{ADMIN_CREW_REPORT_IMPORT_INTERRUPTED_MESSAGE}</p>
        </div>
      )}

      {(result.continuationPending || result.retryableItemsPending) && (
        <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
          {ADMIN_CREW_REPORT_IMPORT_CONTINUATION_MESSAGE}
        </p>
      )}
    </div>
  );
};

export default AdminCrewReportImportSummary;
